/**
 * Subjective Check-In Form - Manual daily self-assessment
 * 1-5 sliders for energy, stress, soreness, motivation
 * Optional nutrition inputs (calories, macros) and notes
 */

import React, { useState } from 'react';
import { Button, Alert } from './ui';
import {
  BoltIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';

interface SubjectiveCheckInFormProps {
  onSuccess?: () => void;
  defaultDate?: string;
}

const SubjectiveCheckInForm: React.FC<SubjectiveCheckInFormProps> = ({ onSuccess, defaultDate }) => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(defaultDate || today);
  const [energy, setEnergy] = useState<number>(3);
  const [stress, setStress] = useState<number>(3);
  const [soreness, setSoreness] = useState<number>(3);
  const [motivation, setMotivation] = useState<number>(3);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const payload: any = {
        date,
        energy_1_5: energy,
        stress_1_5: stress,
        soreness_1_5: soreness,
        motivation_1_5: motivation,
        notes
      };



      await apiClient.post('/api/subjective-checkins/', payload);

      setSuccess(true);
      // Reset form
      setEnergy(3);
      setStress(3);
      setSoreness(3);
      setMotivation(3);
      setNotes('');

      if (onSuccess) {
        setTimeout(onSuccess, 1000);
      }
    } catch (err: any) {
      console.error('Error submitting check-in:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to submit check-in');
    } finally {
      setLoading(false);
    }
  };

  const getRatingLabel = (value: number, metric: string) => {
    if (metric === 'stress' || metric === 'soreness') {
      const labels = ['Very Low', 'Low', 'Moderate', 'High', 'Very High'];
      return labels[value - 1];
    } else {
      const labels = ['Very Low', 'Low', 'Moderate', 'High', 'Very High'];
      return labels[value - 1];
    }
  };

  const getRatingColor = (value: number, metric: string) => {
    if (metric === 'stress' || metric === 'soreness') {
      // Inverted: lower is better
      if (value <= 2) return COLORS.status.success;
      if (value <= 3) return COLORS.status.warning;
      return COLORS.status.error;
    } else {
      // Normal: higher is better
      if (value >= 4) return COLORS.status.success;
      if (value >= 3) return COLORS.status.warning;
      return COLORS.status.error;
    }
  };

  const SliderInput = ({ value, onChange, label, icon: Icon, metric }: { value: number; onChange: (val: number) => void; label: string; icon: any; metric: string }) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginBottom: SPACING[8] }}>
        <Icon style={{ width: 18, height: 18, color: COLORS.text.secondary }} />
        <label style={{
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary
        }}>
          {label}
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
        <input
          type="range"
          min="1"
          max="5"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          style={{
            flex: 1,
            accentColor: getRatingColor(value, metric)
          }}
        />
        <div style={{
          minWidth: 100,
          padding: `${SPACING[4]} ${SPACING[8]}`,
          backgroundColor: `${getRatingColor(value, metric)}10`,
          border: `1px solid ${getRatingColor(value, metric)}`,
          borderRadius: RADIUS,
          textAlign: 'center'
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.semibold,
            color: getRatingColor(value, metric)
          }}>
            {value} - {getRatingLabel(value, metric)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={{
      padding: SPACING[24],
      backgroundColor: COLORS.bg.panel,
      borderRadius: RADIUS,
      border: `1px solid ${COLORS.border.default}`
    }}>
      <h3 style={{
        margin: `0 0 ${SPACING[24]}`,
        fontSize: TYPOGRAPHY.size.lg,
        fontWeight: TYPOGRAPHY.weight.semibold,
        color: COLORS.text.primary
      }}>
        Daily Check-In
      </h3>

      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {success && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="success">Check-in submitted successfully!</Alert>
        </div>
      )}

      {/* Date */}
      <div style={{ marginBottom: SPACING[24] }}>
        <label style={{
          display: 'block',
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          marginBottom: SPACING[8]
        }}>
          Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={today}
          required
          style={{
            width: '100%',
            padding: SPACING[12],
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            color: COLORS.text.primary,
            fontSize: TYPOGRAPHY.size.sm
          }}
        />
      </div>

      {/* Subjective Ratings */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACING[24],
        marginBottom: SPACING[24]
      }}>
        <SliderInput
          value={energy}
          onChange={setEnergy}
          label="Energy Level"
          icon={BoltIcon}
          metric="energy"
        />
        <SliderInput
          value={stress}
          onChange={setStress}
          label="Stress Level"
          icon={ExclamationTriangleIcon}
          metric="stress"
        />
        <SliderInput
          value={soreness}
          onChange={setSoreness}
          label="Muscle Soreness"
          icon={HeartIcon}
          metric="soreness"
        />
        <SliderInput
          value={motivation}
          onChange={setMotivation}
          label="Motivation"
          icon={FireIcon}
          metric="motivation"
        />
      </div>

      {/* Notes */}
      <div style={{ marginBottom: SPACING[24] }}>
        <label style={{
          display: 'block',
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          marginBottom: SPACING[8]
        }}>
          Notes (Optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How are you feeling today? Any observations?"
          rows={3}
          style={{
            width: '100%',
            padding: SPACING[12],
            backgroundColor: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            color: COLORS.text.primary,
            fontSize: TYPOGRAPHY.size.sm,
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        style={{ width: '100%' }}
      >
        {loading ? 'Submitting...' : 'Submit Check-In'}
      </Button>
    </form>
  );
};

export default SubjectiveCheckInForm;
